import { useRef, useEffect, useCallback } from 'react';

interface UseSwipeDrawerOptions {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  width: number;
  swipeable: boolean;
}

const EDGE_THRESHOLD = 20;
const DIRECTION_LOCK_PX = 10;
const VELOCITY_THRESHOLD = 0.3; // px/ms
const DISTANCE_THRESHOLD = 0.5; // fraction of drawer width

export function useSwipeDrawer({
  open,
  onOpenChange,
  width,
  swipeable,
}: UseSwipeDrawerOptions) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const edgeRef = useRef<HTMLDivElement>(null);

  // Set by the hook after a swipe snap so the Drawer skips its own animation
  const didSwipeRef = useRef(false);

  // Mutable drag state — no re-renders during drag
  const drag = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startTime: 0,
    currentX: 0,
    locked: false, // direction decided
    horizontal: false,
    source: '' as 'edge' | 'panel' | '',
  });

  const setDrawerTransform = useCallback(
    (translateX: number, transition: string) => {
      const el = drawerRef.current;
      if (!el) return;
      el.style.transition = transition;
      el.style.transform = `translateX(${translateX}px)`;
      if (transition === 'none') {
        el.style.willChange = 'transform';
      } else {
        el.style.willChange = '';
      }
    },
    [],
  );

  const setBackdropOpacity = useCallback(
    (opacity: number, transition: string) => {
      const el = backdropRef.current;
      if (!el) return;
      el.style.transition = transition;
      el.style.opacity = String(opacity);
    },
    [],
  );

  // Make overlay container visible during edge swipe
  const setOverlayVisible = useCallback((visible: boolean) => {
    const panel = drawerRef.current;
    if (!panel) return;
    const overlay = panel.parentElement;
    if (!overlay) return;
    overlay.style.pointerEvents = visible ? 'auto' : 'none';
  }, []);

  // --- Swipe-to-open from edge ---
  useEffect(() => {
    if (!swipeable || open) return;
    const edgeEl = edgeRef.current;
    if (!edgeEl) return;

    function onTouchStart(e: TouchEvent) {
      const touch = e.touches[0];
      if (touch.clientX > EDGE_THRESHOLD) return;
      drag.current = {
        active: true,
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        currentX: touch.clientX,
        locked: false,
        horizontal: false,
        source: 'edge',
      };
      // Make overlay visible so the user sees the drawer peeking in
      setOverlayVisible(true);
    }

    function onTouchMove(e: TouchEvent) {
      const d = drag.current;
      if (!d.active || d.source !== 'edge') return;
      const touch = e.touches[0];
      const dx = touch.clientX - d.startX;
      const dy = touch.clientY - d.startY;

      if (!d.locked) {
        if (Math.abs(dx) + Math.abs(dy) < DIRECTION_LOCK_PX) return;
        d.locked = true;
        d.horizontal = Math.abs(dx) > Math.abs(dy);
        if (!d.horizontal) {
          d.active = false;
          setOverlayVisible(false);
          return;
        }
      }

      e.preventDefault();
      d.currentX = touch.clientX;
      const progress = Math.min(Math.max(dx / width, 0), 1);
      setDrawerTransform(-width + progress * width, 'none');
      setBackdropOpacity(progress, 'none');
    }

    function onTouchEnd() {
      const d = drag.current;
      if (!d.active || d.source !== 'edge') return;
      d.active = false;

      const dx = d.currentX - d.startX;
      const dt = Date.now() - d.startTime;
      const velocity = dx / Math.max(dt, 1);
      const progress = dx / width;

      if (velocity > VELOCITY_THRESHOLD || progress > DISTANCE_THRESHOLD) {
        // Snap open — hook handles animation, tell Drawer to skip its own
        didSwipeRef.current = true;
        setDrawerTransform(0, 'transform 250ms ease-out');
        setBackdropOpacity(1, 'opacity 250ms ease-out');
        onOpenChange(true);
      } else {
        // Snap back closed
        setDrawerTransform(-width, 'transform 250ms ease-out');
        setBackdropOpacity(0, 'opacity 250ms ease-out');
        setOverlayVisible(false);
      }
    }

    edgeEl.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      edgeEl.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [
    swipeable,
    open,
    width,
    onOpenChange,
    setDrawerTransform,
    setBackdropOpacity,
    setOverlayVisible,
  ]);

  // --- Swipe-to-close on open panel ---
  useEffect(() => {
    if (!swipeable || !open) return;
    const panelEl = drawerRef.current;
    if (!panelEl) return;

    function onTouchStart(e: TouchEvent) {
      const touch = e.touches[0];
      drag.current = {
        active: true,
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        currentX: touch.clientX,
        locked: false,
        horizontal: false,
        source: 'panel',
      };
    }

    function onTouchMove(e: TouchEvent) {
      const d = drag.current;
      if (!d.active || d.source !== 'panel') return;
      const touch = e.touches[0];
      const dx = touch.clientX - d.startX;
      const dy = touch.clientY - d.startY;

      if (!d.locked) {
        if (Math.abs(dx) + Math.abs(dy) < DIRECTION_LOCK_PX) return;
        d.locked = true;
        d.horizontal = Math.abs(dx) > Math.abs(dy);
        if (!d.horizontal || dx > 0) {
          // Vertical scroll or swiping right (already open) → bail
          d.active = false;
          return;
        }
      }

      e.preventDefault();
      d.currentX = touch.clientX;
      const offset = Math.min(Math.max(dx, -width), 0);
      const progress = 1 + offset / width; // 1 → 0
      setDrawerTransform(offset, 'none');
      setBackdropOpacity(progress, 'none');
    }

    function onTouchEnd() {
      const d = drag.current;
      if (!d.active || d.source !== 'panel') return;
      d.active = false;

      const dx = d.currentX - d.startX;
      const dt = Date.now() - d.startTime;
      const velocity = -dx / Math.max(dt, 1); // positive = closing
      const progress = -dx / width;

      if (velocity > VELOCITY_THRESHOLD || progress > DISTANCE_THRESHOLD) {
        // Snap closed — hook handles animation, tell Drawer to skip its own
        didSwipeRef.current = true;
        setDrawerTransform(-width, 'transform 250ms ease-out');
        setBackdropOpacity(0, 'opacity 250ms ease-out');
        onOpenChange(false);
      } else {
        // Snap back open
        setDrawerTransform(0, 'transform 250ms ease-out');
        setBackdropOpacity(1, 'opacity 250ms ease-out');
      }
    }

    panelEl.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      panelEl.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [
    swipeable,
    open,
    width,
    onOpenChange,
    setDrawerTransform,
    setBackdropOpacity,
  ]);

  return { drawerRef, backdropRef, edgeRef, didSwipeRef };
}
